import type { UUID } from '@cmg/common';
import { errorUtil, loggerUtil, UnsavedChangesGuard } from '@cmg/common';
import { UnsavedChangesDialog } from '@cmg/design-system';
import type { FormikContextType } from 'formik';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type FormContext = Pick<
  FormikContextType<unknown>,
  'dirty' | 'isValid' | 'isSubmitting' | 'handleReset' | 'submitForm'
> & { onDiscardAndLeave?: () => void };

export type FormikUnsavedChangesGuardContextType = {
  registerForm: (formId: UUID, fomContext: FormContext) => void;
  unregisterForm: (formId: UUID) => void;
};

const FormikUnsavedChangesGuardContext = createContext<FormikUnsavedChangesGuardContextType>({
  registerForm: () => {
    throw new Error('FormikUnsavedChangesGuardContext is not provided');
  },
  unregisterForm: () => {
    throw new Error('FormikUnsavedChangesGuardContext is not provided');
  },
});

export type FormikUnsavedChangesGuardContextProviderProps = React.PropsWithChildren<{
  submitInReverseOrder?: boolean;
}>;
export const FormikUnsavedChangesGuardContextProvider: React.FC<
  FormikUnsavedChangesGuardContextProviderProps
> = ({ children, submitInReverseOrder }) => {
  const [forms, setForms] = useState<Record<UUID, FormContext>>({});

  const handleRegisterForm = useCallback((formId: UUID, formContext: FormContext) => {
    setForms(prevForms => {
      return { ...prevForms, [formId]: formContext };
    });
  }, []);

  const handleUnregisterForm = useCallback((formId: UUID) => {
    setForms(prevForms => {
      return omit(prevForms, formId);
    });
  }, []);

  const handleResetForms = useCallback(() => {
    Object.values(forms).forEach(form => {
      form.handleReset();
      form.onDiscardAndLeave?.();
    });
  }, [forms]);

  const handleSubmitForms = useCallback(async () => {
    const formsToSave = Object.entries(forms)
      .filter(([_, form]) => form.dirty && form.isValid)
      .sort(([a], [b]) => (submitInReverseOrder ? b.localeCompare(a) : a.localeCompare(b)))
      .map(([_, form]) => form);

    for (const form of formsToSave) {
      try {
        await form.submitForm();
      } catch (e) {
        // @ts-expect-error
        errorUtil.assertIsError(e);
        // @ts-expect-error
        loggerUtil.error(e);
      }
    }
  }, [forms, submitInReverseOrder]);

  const { hasUnsavedChanges, ...dialogProps } = useMemo(() => {
    const fomContexts = Object.values(forms);

    return {
      hasUnsavedChanges: fomContexts.some(form => form.dirty),
      isSaving: fomContexts.some(form => form.isSubmitting),
      isSaveButtonDisabled: !fomContexts.some(form => form.dirty && form.isValid),
      onDiscardAndLeave: handleResetForms,
      onSaveAndLeave: handleSubmitForms,
    };
  }, [forms, handleResetForms, handleSubmitForms]);

  return (
    <FormikUnsavedChangesGuardContext.Provider
      value={{ registerForm: handleRegisterForm, unregisterForm: handleUnregisterForm }}
    >
      <UnsavedChangesGuard
        when={hasUnsavedChanges}
        Dialog={UnsavedChangesDialog}
        dialogProps={dialogProps}
      />
      {children}
    </FormikUnsavedChangesGuardContext.Provider>
  );
};

export const useFormikUnsavedChangesGuardContext = (): FormikUnsavedChangesGuardContextType => {
  const context = useContext(FormikUnsavedChangesGuardContext);

  if (isNil(context)) {
    throw new Error(
      'useFormikUnsavedChangesGuardContext must be used within a FormikUnsavedChangesGuardContextProvider'
    );
  }

  return context;
};
